import { DatePipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MESSAGE_CONSTANTS, NIGHTWEB_APP_CONSTANT } from 'src/app/constants';
import { DesignBrief, WatchThemeStatus } from 'src/app/models';
import { ToasterService, WatchThemeService } from 'src/app/services';
import {
  NightwebUtil,
  buildWatchThemeKey,
  formatFileSizeLabel,
  watchThemeBriefDisplayType,
  watchThemeBriefKind,
} from 'src/app/utils';
import { WatchThemeBriefModalComponent } from '../watch-theme-brief-modal/watch-theme-brief-modal.component';

@Component({
  selector: 'app-add-edit-watch-theme-modal',
  templateUrl: './add-edit-watch-theme-modal.component.html',
  styleUrls: ['./add-edit-watch-theme-modal.component.scss'],
  standalone: false,
})
export class AddEditWatchThemeModalComponent implements OnInit, OnDestroy {
  themeForm!: FormGroup;
  loading = false;
  submitted = false;
  title = '';
  formData: any = {};
  nameError = '';
  briefFile: File | null = null;
  pendingBrief: DesignBrief | null = null;
  isEdit = false;
  isDragOver = false;
  statusOptions = [
    { label: WatchThemeStatus.AVAILABLE, value: WatchThemeStatus.AVAILABLE },
    { label: WatchThemeStatus.PENDING_BUILD, value: WatchThemeStatus.PENDING_BUILD },
    { label: WatchThemeStatus.RETIRED, value: WatchThemeStatus.RETIRED },
  ];
  acceptedExtensions = '.png,.jpg,.jpeg,.pdf,.html,.txt,.md';
  @ViewChild('briefInput') briefInput!: ElementRef<HTMLInputElement>;
  private initialValues: Record<string, any> = {};
  private nameSub?: Subscription;
  private statusSub?: Subscription;
  private versionSub?: Subscription;
  private defaultSub?: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private toasterService: ToasterService,
    private watchThemeService: WatchThemeService,
    private nightwebUtil: NightwebUtil,
    private modalService: MdbModalService,
    private datePipe: DatePipe,
    public modalRef: MdbModalRef<AddEditWatchThemeModalComponent>,
  ) {}

  ngOnInit(): void {
    this.isEdit = this.formData?.mode === 'edit' || !!this.formData?.id;
    this.themeForm = this.formBuilder.group({
      name: [this.formData?.name || '', Validators.required],
      uniqueKey: [{ value: this.formData?.uniqueKey || '', disabled: true }],
      description: [this.formData?.description || ''],
      status: [this.formData?.status || WatchThemeStatus.PENDING_BUILD],
      watchAppVersion: [this.formData?.watchAppVersion || ''],
      isDefault: [!!this.formData?.isDefault],
    });
    this.nameSub = this.themeForm.controls['name'].valueChanges.subscribe((name: string) => {
      if (!this.isEdit) {
        this.themeForm.controls['uniqueKey'].setValue(buildWatchThemeKey(name || ''));
      }
      this.nameError = '';
    });
    if (this.isEdit) {
      if (this.formData?.isDefault) {
        this.themeForm.controls['isDefault'].disable({ emitEvent: false });
      }
      this.applyDefaultStatusOptions(this.themeForm.getRawValue().isDefault);
      this.applyVersionValidators(this.themeForm.value.status);
      this.syncDefaultAvailability();
      this.statusSub = this.themeForm.controls['status'].valueChanges.subscribe((status: string) => {
        this.applyVersionValidators(status);
        this.syncDefaultAvailability();
      });
      this.versionSub = this.themeForm.controls['watchAppVersion'].valueChanges.subscribe(() => {
        this.syncDefaultAvailability();
      });
      this.defaultSub = this.themeForm.controls['isDefault'].valueChanges.subscribe((isDefault: boolean) => {
        this.applyDefaultStatusOptions(isDefault);
      });
    }
    this.initialValues = this.themeForm.getRawValue();
  }

  ngOnDestroy(): void {
    this.nameSub?.unsubscribe();
    this.statusSub?.unsubscribe();
    this.versionSub?.unsubscribe();
    this.defaultSub?.unsubscribe();
  }

  get organizationCount(): number {
    return Number(this.formData?.organizationCount || 0);
  }

  /** Last default cannot be unchecked here — promote another Available theme first. */
  get isCurrentDefault(): boolean {
    return !!this.formData?.isDefault;
  }

  get existingBrief(): DesignBrief | null {
    return this.formData?.designBrief || null;
  }

  get displayBrief(): DesignBrief | null {
    return this.pendingBrief || this.existingBrief;
  }

  get showDropZone(): boolean {
    return !this.displayBrief;
  }

  /** Nothing to save until a field actually changed or a new brief was attached. */
  get hasChanges(): boolean {
    if (this.briefFile) {
      return true;
    }
    const raw = this.themeForm.getRawValue();
    return Object.keys(this.initialValues).some(
      (key) => this.normalize(raw[key]) !== this.normalize(this.initialValues[key]),
    );
  }

  get canSave(): boolean {
    if (!this.themeForm || this.themeForm.invalid || this.nameError) {
      return false;
    }
    const raw = this.themeForm.getRawValue();
    if (!(raw.name || '').trim()) {
      return false;
    }
    if (this.isEdit && raw.status === WatchThemeStatus.AVAILABLE && !(raw.watchAppVersion || '').trim()) {
      return false;
    }
    return this.hasChanges;
  }

  private normalize(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return typeof value === 'string' ? value.trim() : String(value);
  }

  get briefMeta(): string {
    const brief = this.displayBrief;
    if (!brief) {
      return '';
    }
    const attachedAt = brief.attachedAt
      ? this.datePipe.transform(brief.attachedAt, 'MMM d, y')
      : '';
    return [
      brief.displayType,
      brief.sizeLabel,
      brief.attachedBy ? `attached by ${brief.attachedBy}` : '',
      attachedAt,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  applyDefaultStatusOptions(isDefault: boolean) {
    const allStatuses = [
      { label: WatchThemeStatus.AVAILABLE, value: WatchThemeStatus.AVAILABLE },
      { label: WatchThemeStatus.PENDING_BUILD, value: WatchThemeStatus.PENDING_BUILD },
      { label: WatchThemeStatus.RETIRED, value: WatchThemeStatus.RETIRED },
    ];
    this.statusOptions = isDefault
      ? allStatuses.filter((option) => option.value !== WatchThemeStatus.RETIRED)
      : allStatuses;
  }

  /** Backend rule: only an Available theme with a shipped version can be the default. */
  get canBeDefault(): boolean {
    const raw = this.themeForm.getRawValue();
    return (
      raw.status === WatchThemeStatus.AVAILABLE && !!(raw.watchAppVersion || '').trim()
    );
  }

  private syncDefaultAvailability() {
    if (this.isCurrentDefault) {
      return;
    }
    const control = this.themeForm.controls['isDefault'];
    if (this.canBeDefault) {
      if (control.disabled) {
        control.enable({ emitEvent: false });
      }
      return;
    }
    if (control.value) {
      control.setValue(false, { emitEvent: false });
      this.applyDefaultStatusOptions(false);
    }
    if (control.enabled) {
      control.disable({ emitEvent: false });
    }
  }

  applyVersionValidators(status: string) {
    const control = this.themeForm.controls['watchAppVersion'];
    if (status === WatchThemeStatus.AVAILABLE) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  openFilePicker() {
    this.briefInput?.nativeElement.click();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setBriefFile(file);
    }
  }

  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.setBriefFile(file);
    }
    input.value = '';
  }

  setBriefFile(file: File) {
    const kind = watchThemeBriefKind(file.name);
    this.briefFile = file;
    this.pendingBrief = {
      fileName: file.name,
      kind,
      displayType: watchThemeBriefDisplayType(kind),
      sizeLabel: formatFileSizeLabel(file.size),
      attachedBy: 'NightWatch Admin',
      attachedAt: new Date().toISOString(),
    };
  }

  removeBrief() {
    this.briefFile = null;
    this.pendingBrief = null;
  }

  openBrief() {
    const brief = this.displayBrief;
    if (!brief) {
      return;
    }
    const open = (preview: DesignBrief) => {
      const config = this.nightwebUtil.shallowCopy(NIGHTWEB_APP_CONSTANT.modalConfig);
      config.data.title = preview.fileName;
      config.modalClass = 'modal-dialog-scrollable modal-md';
      config.data.formData = {
        themeId: this.briefFile ? null : this.formData?.id,
        brief: preview,
      };
      this.modalService.open(WatchThemeBriefModalComponent, config);
    };

    if (this.briefFile) {
      const kind = watchThemeBriefKind(this.briefFile.name);
      if (kind === 'txt' || kind === 'md' || kind === 'html') {
        this.briefFile.text().then((content) => open({ ...brief, content }));
        return;
      }
      if (kind === 'png' || kind === 'jpg' || kind === 'pdf') {
        open({ ...brief, url: URL.createObjectURL(this.briefFile) });
        return;
      }
    }
    open(brief);
  }

  close() {
    this.modalRef.close();
  }

  save() {
    if (!this.canSave) {
      return;
    }

    const raw = this.themeForm.getRawValue();
    const version = (raw.watchAppVersion || '').trim();
    const payload = new FormData();
    payload.append('name', raw.name.trim());
    payload.append('uniqueKey', raw.uniqueKey);
    payload.append('description', raw.description || '');
    if (this.isEdit) {
      payload.append('status', raw.status);
      payload.append('watchAppVersion', version);
      payload.append('isDefault', raw.isDefault ? 'true' : 'false');
    }
    if (this.briefFile) {
      payload.append('brief', this.briefFile);
    }

    this.loading = true;
    const request$ = this.isEdit
      ? this.watchThemeService.update(this.formData.id, payload)
      : this.watchThemeService.create(payload);

    request$.pipe(finalize(() => (this.loading = false))).subscribe({
      next: (response) => {
        this.toasterService.show(
          'Success',
          this.isEdit
            ? MESSAGE_CONSTANTS.WATCH_THEME_SAVED
            : MESSAGE_CONSTANTS.WATCH_THEME_SAVED_PENDING,
        );
        this.modalRef.close(response.data);
      },
      error: (error) => {
        const message = (error?.error?.message || '').toLowerCase();
        if (message.includes('already exists')) {
          this.nameError = error.error.message;
          this.themeForm.controls['name'].markAsTouched();
        }
      },
    });
  }
}
